import { supabase } from '@/integrations/supabase/client';

// Sample categories data for initialization
const SAMPLE_CATEGORIES = [
  {
    id: '1',
    name: 'Photography',
    image_url: 'https://images.unsplash.com/photo-1554080353-a576cf803bda?ixlib=rb-4.0.3',
    slug: 'photography',
    description: 'Professional photography services',
    count: 10,
    trending: true,
  },
  {
    id: '2',
    name: 'Videography',
    image_url: 'https://images.unsplash.com/photo-1575318634028-6a0cfcb60c59?ixlib=rb-4.0.3',
    slug: 'videography',
    description: 'Professional video production services',
    count: 8,
    trending: true,
  },
];

// Sample gigs for initialization
const SAMPLE_GIGS = [
  {
    id: '1',
    title: 'Professional Portrait Photography Session',
    description: 'I will take stunning portrait photos of you or your family.',
    category: 'Photography',
    subcategory: 'portrait-photography',
    images: ['https://images.unsplash.com/photo-1542909168-82c3e7fdca5c?ixlib=rb-4.0.3'],
    basic_package: {
      title: 'Basic Package',
      description: '30 minute session with 5 edited photos',
      price: 100,
      delivery: 3,
      revisions: 1,
      features: ['30 minute session', '5 professionally edited photos', 'High-resolution digital files']
    },
    standard_package: {
      title: 'Standard Package',
      description: '1 hour session with 10 edited photos',
      price: 175,
      delivery: 5,
      revisions: 2,
      features: ['1 hour session', '10 professionally edited photos', 'High-resolution digital files']
    },
    premium_package: {
      title: 'Premium Package',
      description: '2 hour session with 20 edited photos',
      price: 250,
      delivery: 7,
      revisions: 3,
      features: ['2 hour session', '20 professionally edited photos', 'High-resolution digital files']
    },
    user_id: '00000000-0000-0000-0000-000000000000',
    trending: true,
    featured: true,
    rating: 4.8,
    review_count: 128,
    views: 1500
  },
  {
    id: '2',
    title: 'Professional Wedding Video Production',
    description: 'I will create a beautiful wedding video that captures all the special moments of your big day.',
    category: 'Videography',
    subcategory: 'wedding-videography',
    images: ['https://images.unsplash.com/photo-1632333528660-bcbd6bd5f260?ixlib=rb-4.0.3'],
    basic_package: {
      title: 'Basic Package',
      description: '4 hours of wedding video coverage',
      price: 500,
      delivery: 14,
      revisions: 1,
      features: ['4 hours of coverage', '5-minute highlight video', 'Digital delivery']
    },
    standard_package: {
      title: 'Standard Package',
      description: '8 hours of wedding video coverage',
      price: 800,
      delivery: 21,
      revisions: 2,
      features: ['8 hours of coverage', '10-minute highlight video', 'Full ceremony video']
    },
    premium_package: {
      title: 'Premium Package',
      description: 'Full day wedding video coverage',
      price: 1200,
      delivery: 30,
      revisions: 3,
      features: ['Full day coverage', '15-minute highlight video', 'Full ceremony video']
    },
    user_id: '00000000-0000-0000-0000-000000000000',
    trending: true,
    featured: false,
    rating: 4.9,
    review_count: 89,
    views: 1200
  }
];

/**
 * Initialize the database with sample data if it's empty
 * @returns Promise resolving to true if initialization succeeded
 */
export const initializeDatabase = async (): Promise<boolean> => {
  try {
    console.log('Checking if database needs initialization...');

    // Check if categories table exists and has data
    const { data: existingCategories, error: categoryError } = await supabase
      .from('categories')
      .select('id')
      .limit(1);

    if (categoryError) {
      console.error('Error checking categories:', categoryError);
      if (categoryError.message.includes('relation "categories" does not exist')) {
        console.log('Categories table does not exist. Tables need to be created first.');
        return false;
      }
    }

    // If no categories exist, insert sample data
    if (!existingCategories || existingCategories.length === 0) {
      console.log('No categories found. Initializing database with sample data...');

      // Insert categories
      const { error: categoriesError } = await supabase
        .from('categories')
        .insert(SAMPLE_CATEGORIES);

      if (categoriesError) {
        console.error('Error inserting categories:', categoriesError);
        return false;
      }

      console.log('✅ Categories inserted successfully');

      // Insert gigs
      const { error: gigsError } = await supabase
        .from('gigs')
        .insert(SAMPLE_GIGS);

      if (gigsError) {
        console.error('Error inserting gigs:', gigsError);
        return false;
      }

      console.log('✅ Gigs inserted successfully');
      console.log('Database initialization completed!');
      return true;
    } else {
      console.log('Database already contains data. Skipping initialization.');
      return true;
    }
  } catch (error) {
    console.error('Error initializing database:', error);
    return false;
  }
};

export default initializeDatabase; 